export const Currency = {
  AUD: "AUD",
  BRL: "BRL",
  CAD: "CAD",
  CNY: "CNY",
  EUR: "EUR",
  GBP: "GBP",
  HKD: "HKD",
  ILS: "ILS",
  INR: "INR",
  JPY: "JPY",
  KRW: "KRW",
  MYR: "MYR",
  NZD: "NZD",
  PHP: "PHP",
  SEK: "SEK",
  SGD: "SGD",
  TBH: "THB",
  TWD: "TWD",
  USD: "USD",
} as const;

export type CurrencyType = (typeof Currency)[keyof typeof Currency];

export const Iso639 = {
  english: "en",
  thai: "th",
  chinese: "zh",
  taiwanese: "zh_tw",
  japanese: "ja",
  korean: "ko",
} as const;
export type Iso639Type = (typeof Iso639)[keyof typeof Iso639];

export const Iso3166 = {
  china: "CHN",
  japan: "JPN",
  southKorea: "KOR",
  taiwan: "TWN",
  thailand: "THA",
  unitedKingdom: "GBR",
  unitedStates: "USA",
};
export type Iso3166Type = (typeof Iso3166)[keyof typeof Iso3166];
const currencyMap = new Map<Iso3166Type, CurrencyType>([
  [Iso3166.china, Currency.CNY],
  [Iso3166.japan, Currency.JPY],
  [Iso3166.southKorea, Currency.KRW],
  [Iso3166.taiwan, Currency.TWD],
  [Iso3166.thailand, Currency.TBH],
  [Iso3166.unitedKingdom, Currency.GBP],
  [Iso3166.unitedStates, Currency.USD],
]);

const invertMap = <K, V>(originalMap: Map<K, V>) =>
  new Map(Array.from(originalMap).map(([key, val]) => [val, key]) as [V, K][]);

const inverseCurrencyMap = invertMap(currencyMap);

const flagMap = new Map<Iso3166Type, string>([
  [Iso3166.china, "🇨🇳"],
  [Iso3166.japan, "🇯🇵"],
  [Iso3166.southKorea, "🇰🇷"],
  [Iso3166.taiwan, "🇹🇼"],
  [Iso3166.thailand, "🇹🇭"],
  [Iso3166.unitedKingdom, "🇬🇧"],
  [Iso3166.unitedStates, "🇺🇸"],
]);

const currencySymbolMap = new Map<CurrencyType, string>([
  [Currency.AUD, "A$"],
  [Currency.BRL, "R$"],
  [Currency.CAD, "C$"],
  [Currency.CNY, "¥"],
  [Currency.EUR, "€"],
  [Currency.GBP, "£"],
  [Currency.HKD, "HK$"],
  [Currency.ILS, "₪"],
  [Currency.INR, "₹"],
  [Currency.JPY, "¥"],
  [Currency.KRW, "₩"],
  [Currency.MYR, "RM"],
  [Currency.NZD, "NZ$"],
  [Currency.PHP, "₱"],
  [Currency.SEK, "kr"],
  [Currency.SGD, "S$"],
  [Currency.TBH, "฿"],
  [Currency.TWD, "NT$"],
  [Currency.USD, "US$"],
]);

export function getCurrencySymbol(currency: CurrencyType) {
  return currencySymbolMap.get(currency);
}

export function getCurrency(iso3166: Iso3166Type) {
  return currencyMap.get(iso3166);
}

export function getFlag(iso3166: Iso3166Type) {
  return flagMap.get(iso3166);
}

export function getIso3166FromCurrency(currency: CurrencyType) {
  return inverseCurrencyMap.get(currency);
}
